import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, merge, NEVER, Observable, ReplaySubject, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { BaseContainer, FormSelectOption } from '@stylehaven/components';
import { Identifiable } from '../../../entities/common/identifiable.interface';
import {
    RecordSecurityGroups,
    RecordSecurityGroupUpdate
} from '../../../entities/management/record-security-groups.entity';
import { SecurityGroup } from '../../../entities/management/security-group.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { waitForIndex } from '../../../helpers/wait-for-index.helper';
import { RecordSecurityGroupsSandboxService } from '../../sandbox/record-security-groups-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-security-groups-edit-container',
    templateUrl: './record-security-groups-edit-container.component.html',
    styleUrls: ['./record-security-groups-edit-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordSecurityGroupsEditContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    readonly selectedSecurityGroupIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    readonly securityGroupsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    readonly savingInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly seriesSavingError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    seriesSecurityGroups$!: Observable<SecurityGroup[]>;
    selectedSecurityGroups$!: Observable<FormSelectOption[]>;
    readonlySecurityGroups$!: Observable<SecurityGroup[]>;
    editableSeriesSecurityGroups$!: Observable<SecurityGroup[]>;
    allSecurityGroupsSelectOptions$!: Observable<FormSelectOption[]>;
    securityGroupsHaveChanged$!: Observable<boolean>;
    record$: Subject<Identifiable> = new ReplaySubject<Identifiable>(1);
    save$: Subject<void> = new Subject();

    form!: UntypedFormGroup;

    @Input()
    set record(record: Identifiable) {
        this.record$.next(record);
    }

    @Output()
    genericError: EventEmitter<Error> = new EventEmitter();

    @Output()
    resulted: EventEmitter<RecordSecurityGroupUpdate> = new EventEmitter();

    @Output()
    valueChanged: EventEmitter<boolean> = new EventEmitter();

    constructor(
        private readonly sandbox: RecordSecurityGroupsSandboxService,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        this.initForm();
        this.initSourceStreams();
        this.emitResult();
        this.emitSecurityGroupsHaveChangedStream();
    }

    ngOnDestroy(): void {
        this.seriesSavingError$.complete();
        this.selectedSecurityGroupIds$.complete();
        this.savingInProgress$.complete();
        this.save$.complete();
    }

    removeSecurityGroup(securityGroup: FormSelectOption): void {
        const currentValue: string[] = this.selectedSecurityGroupIds$.value;
        const index: number = currentValue.indexOf(securityGroup.value);

        currentValue.splice(index, 1);

        this.selectedSecurityGroupIds$.next(currentValue);
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            SecurityGroups: [null]
        });

        this.listenToFormChanges();
    }

    private listenToFormChanges(): void {
        this.securityGroupsControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                filter(value => !!value && typeof value === 'string'),
                tap((securityGroupId: string) => {
                    this.setSelectedGroupIds([securityGroupId]);
                    this.securityGroupsControl.setValue('', { emitEvent: false });
                })
            )
            .subscribe();
    }

    private emitResult() {
        combineLatest([this.record$, this.selectedSecurityGroupIds$, this.seriesSecurityGroups$])
            .pipe(
                takeUntil(this.destroy$),
                map(([record, selectedSecurityGroupIds, originalSecurityGroups]) => {
                    return {
                        recordId: record.Id,
                        newSecurityGroups: selectedSecurityGroupIds,
                        originalSecurityGroups: originalSecurityGroups
                    } as RecordSecurityGroupUpdate;
                })
            )
            .subscribe(this.setResult.bind(this));
    }

    private setResult(update: RecordSecurityGroupUpdate) {
        this.resulted.next(update);
    }

    private emitSecurityGroupsHaveChangedStream(): void {
        combineLatest([this.selectedSecurityGroupIds$, this.seriesSecurityGroups$])
            .pipe(
                takeUntil(this.destroy$),
                map(
                    ([selectedIds, currentSeriesSecurityGroup]) =>
                        !isEqual(
                            selectedIds,
                            currentSeriesSecurityGroup
                                .filter(securityGroup => !securityGroup.IsReadOnly)
                                .map(securityGroup => securityGroup.Id)
                        )
                )
            )
            .subscribe(this.setValueChanged.bind(this));
    }

    private setValueChanged(value: boolean) {
        this.valueChanged.next(value);
    }

    private initSourceStreams(): void {
        this.seriesSecurityGroups$ = this.getRecordSecurityGroups();
        this.readonlySecurityGroups$ = this.getSeriesReadonlySecurityGroups();
        this.editableSeriesSecurityGroups$ = this.getSeriesEditableSecurityGroups();

        this.allSecurityGroupsSelectOptions$ = this.getAllSecurityGroups();
        this.selectedSecurityGroups$ = this.getSelectedSecurityGroupsStream();
    }

    private getAllSecurityGroups(): Observable<FormSelectOption[]> {
        return this.sandbox.getEditableSecurityGroups().pipe(shareReplay());
    }

    private getSeriesReadonlySecurityGroups(): Observable<SecurityGroup[]> {
        return this.seriesSecurityGroups$.pipe(
            map((seriesSecurityGroups: SecurityGroup[]) =>
                seriesSecurityGroups.filter((securityGroup: SecurityGroup) => securityGroup.IsReadOnly)
            )
        );
    }

    private getSeriesEditableSecurityGroups(): Observable<SecurityGroup[]> {
        return this.seriesSecurityGroups$.pipe(
            map((seriesSecurityGroups: SecurityGroup[]) =>
                seriesSecurityGroups.filter((securityGroup: SecurityGroup) => !securityGroup.IsReadOnly)
            ),
            tap((seriesSecurityGroups: SecurityGroup[]) => {
                const ids = seriesSecurityGroups
                    .filter(securityGroup => securityGroup !== undefined)
                    .map(securityGroup => securityGroup.Id as string);

                this.setSelectedGroupIds(ids);
            })
        );
    }

    private getSelectedSecurityGroupsStream(): Observable<FormSelectOption[]> {
        return merge(
            this.editableSeriesSecurityGroups$.pipe(map(this.sandbox.mapToSelectOption.bind(this))),
            this.getNewSelectedSecurityGroupsStream()
        );
    }

    private getNewSelectedSecurityGroupsStream(): Observable<FormSelectOption[]> {
        return this.selectedSecurityGroupIds$.pipe(
            switchMap((selectedIds: string[]) =>
                this.allSecurityGroupsSelectOptions$.pipe(
                    map(options => options.filter(option => selectedIds.includes(option.value)))
                )
            )
        );
    }

    private getRecordSecurityGroups(): Observable<SecurityGroup[]> {
        this.securityGroupsLoading$.startLoading();

        return this.record$.pipe(
            switchMap((record: Identifiable) => this.sandbox.getSecurityGroupsOfRecord(record)),
            waitForIndex(),
            tap(() => this.securityGroupsLoading$.stopLoading()),
            catchError(this.handleSecurityGroupError.bind(this)),
            map((seriesSecurityGroups: RecordSecurityGroups) => seriesSecurityGroups.SecurityGroups),
            shareReplay()
        );
    }

    private handleSecurityGroupError(err: Error): Observable<never> {
        this.genericError.emit(err);
        return NEVER;
    }

    private setSelectedGroupIds(ids: string[]): void {
        const currentValue = this.selectedSecurityGroupIds$.value;
        currentValue.push(...ids);
        this.selectedSecurityGroupIds$.next([...new Set(currentValue)]);
    }

    get securityGroupsControl(): UntypedFormControl {
        return this.form.get('SecurityGroups') as UntypedFormControl;
    }
}
